/**
 * Centralized audio recording configuration.
 *
 * The preferred MIME type can be overridden via the Vite env variable
 * `VITE_AUDIO_RECORDING_MIME` (e.g. `audio/wav` or `audio/webm;codecs=opus`).
 *
 * When the browser does not support the requested type, MediaRecorder will
 * fall back to its own default codec automatically.
 */

const ENV_MIME = import.meta.env.VITE_AUDIO_RECORDING_MIME as string | undefined;

export const AUDIO_RECORDING_MIME: string | undefined = ENV_MIME || 'audio/wav';

/**
 * Returns MediaRecorder options with the configured MIME type.
 * If the browser does not support the requested type the options object is
 * returned without a mimeType key so the browser picks its default.
 */
export function getMediaRecorderOptions(): MediaRecorderOptions | undefined {
  if (!AUDIO_RECORDING_MIME) return undefined;
  if (typeof MediaRecorder === 'undefined') return undefined;
  if (MediaRecorder.isTypeSupported(AUDIO_RECORDING_MIME)) {
    return { mimeType: AUDIO_RECORDING_MIME };
  }
  console.warn(
    `[audio.config] MIME type "${AUDIO_RECORDING_MIME}" is not supported by this browser — falling back to default.`,
  );
  return undefined;
}

/**
 * Returns the file extension (without dot) for a given MIME type.
 * Defaults to `"wav"` when the type is unknown.
 */
export function getAudioExtension(mimeType: string): string {
  const ext = mimeType.split('/')[1]?.split(';')[0];
  return ext || 'wav';
}
